/**
 * Field Metadata Utilities for AnalysisBuilder
 *
 * Functions for working with field metadata from the schema.
 */

import type { FieldOption, FieldType } from '../types.js'
import type { MetaResponse, MetaField } from '../../../shared/types.js'

/**
 * Get cube name from a field name (e.g., "Employees.count" -> "Employees")
 */
export function getCubeNameFromField(fieldName: string): string {
  return fieldName.split('.')[0]
}

/**
 * Get field short name from full name (e.g., "Employees.count" -> "count")
 */
export function getFieldShortName(fieldName: string): string {
  const parts = fieldName.split('.')
  return parts.length > 1 ? parts.slice(1).join('.') : fieldName
}

/**
 * Find field metadata from schema
 */
export function findFieldInSchema(
  fieldName: string,
  schema: MetaResponse | null
): { field: MetaField; cubeName: string; fieldType: FieldType } | null {
  if (!schema) return null

  for (const cube of schema.cubes) {
    // Check measures
    const measure = cube.measures.find((m) => m.name === fieldName)
    if (measure) {
      return { field: measure, cubeName: cube.name, fieldType: 'measure' }
    }

    // Check dimensions
    const dimension = cube.dimensions.find((d) => d.name === fieldName)
    if (dimension) {
      return {
        field: dimension,
        cubeName: cube.name,
        fieldType: dimension.type === 'time' ? 'timeDimension' : 'dimension'
      }
    }
  }

  return null
}

/**
 * Get display title for a field
 */
export function getFieldTitle(fieldName: string, schema: MetaResponse | null): string {
  const found = findFieldInSchema(fieldName, schema)
  if (found) {
    return found.field.title || found.field.shortTitle || fieldName
  }
  return fieldName
}

/**
 * Determine field type from metadata
 */
export function getFieldType(field: MetaField): FieldType {
  if (field.type === 'time') return 'timeDimension'
  // Measures typically have aggregation types
  if (['count', 'countDistinct', 'sum', 'avg', 'min', 'max', 'runningTotal', 'countDistinctApprox'].includes(field.type)) {
    return 'measure'
  }
  return 'dimension'
}

/**
 * Build a measure field option from cube + measure metadata
 */
function measureToOption(cubeName: string, measure: MetaField): FieldOption {
  return {
    name: measure.name,
    title: measure.title || measure.shortTitle || measure.name,
    shortTitle: measure.shortTitle || measure.title || measure.name,
    type: measure.type,
    description: measure.description,
    cubeName,
    fieldType: 'measure'
  }
}

/**
 * Build a dimension field option from cube + dimension metadata
 */
function dimensionToOption(cubeName: string, dimension: MetaField): FieldOption {
  return {
    name: dimension.name,
    title: dimension.title || dimension.shortTitle || dimension.name,
    shortTitle: dimension.shortTitle || dimension.title || dimension.name,
    type: dimension.type,
    description: dimension.description,
    cubeName,
    fieldType: dimension.type === 'time' ? 'timeDimension' : 'dimension'
  }
}

/**
 * Convert schema to flat list of field options
 *
 * - 'metrics': measures only
 * - 'breakdown' / 'dimensionFilter': dimensions only ('dimensionFilter' is used
 *   for funnel step filters where measures don't work)
 * - 'filter': both measures and dimensions
 */
export function schemaToFieldOptions(
  schema: MetaResponse | null,
  mode: 'metrics' | 'breakdown' | 'filter' | 'dimensionFilter'
): FieldOption[] {
  if (!schema) return []

  const includeMeasures = mode === 'metrics' || mode === 'filter'
  const includeDimensions = mode !== 'metrics'

  const options: FieldOption[] = []

  for (const cube of schema.cubes) {
    if (includeMeasures) {
      for (const measure of cube.measures) {
        options.push(measureToOption(cube.name, measure))
      }
    }
    if (includeDimensions) {
      for (const dimension of cube.dimensions) {
        options.push(dimensionToOption(cube.name, dimension))
      }
    }
  }

  return options
}

/**
 * Filter field options by search term
 */
export function filterFieldOptions(
  options: FieldOption[],
  searchTerm: string,
  selectedCube?: string | null
): FieldOption[] {
  let filtered = options

  // Filter by cube if selected
  if (selectedCube && selectedCube !== 'all') {
    filtered = filtered.filter((opt) => opt.cubeName === selectedCube)
  }

  // Filter by search term
  if (searchTerm.trim()) {
    const term = searchTerm.toLowerCase()
    filtered = filtered.filter(
      (opt) =>
        opt.name.toLowerCase().includes(term) ||
        opt.title.toLowerCase().includes(term) ||
        (opt.description?.toLowerCase().includes(term) ?? false)
    )
  }

  return filtered
}

/**
 * Group field options by cube
 */
export function groupFieldsByCube(options: FieldOption[]): Map<string, FieldOption[]> {
  const grouped = new Map<string, FieldOption[]>()

  for (const option of options) {
    const existing = grouped.get(option.cubeName) || []
    existing.push(option)
    grouped.set(option.cubeName, existing)
  }

  return grouped
}

/**
 * Get list of cube names from schema
 */
export function getCubeNames(schema: MetaResponse | null): string[] {
  if (!schema) return []
  return schema.cubes.map((cube) => cube.name)
}

/**
 * Get cube title by name
 */
export function getCubeTitle(cubeName: string, schema: MetaResponse | null): string {
  if (!schema) return cubeName
  const cube = schema.cubes.find((c) => c.name === cubeName)
  return cube?.title || cubeName
}

/**
 * Get all cubes reachable from a source cube via join relationships
 * Includes the source cube itself plus all cubes it has joins to
 *
 * @param sourceCube - Name of the cube to find related cubes for
 * @param schema - Full schema with all cubes
 * @returns Set of cube names that are reachable from the source
 */
export function getRelatedCubeNames(
  sourceCube: string,
  schema: MetaResponse | null
): Set<string> {
  const related = new Set<string>()

  if (!schema) return related

  // Always include the source cube
  related.add(sourceCube)

  // Find the source cube and get its relationships
  const cube = schema.cubes.find((c) => c.name === sourceCube)
  if (!cube || !cube.relationships) return related

  // Add all directly related cubes
  for (const rel of cube.relationships) {
    related.add(rel.targetCube)
  }

  return related
}

/**
 * Filter schema to include only cubes reachable from a source cube
 * This is used for funnel step filters where cross-cube filtering is supported
 *
 * @param sourceCube - Name of the cube to find related cubes for
 * @param schema - Full schema with all cubes
 * @returns Filtered schema containing only reachable cubes
 */
export function getRelatedCubesSchema(
  sourceCube: string,
  schema: MetaResponse | null
): MetaResponse | null {
  if (!schema) return null

  const relatedNames = getRelatedCubeNames(sourceCube, schema)

  return {
    cubes: schema.cubes
      .filter((c) => relatedNames.has(c.name))
      .map((c) => ({
        ...c,
        description: c.description || '',
      })),
  }
}
